import * as React from "react";

/** exported only for non-react environment, e.g. assignees editor on kanban */
export const createAuditContext = (location?: string): AuditContextValue => ({ location });

export type AuditContextValue = { location: string };
export const AuditContext = React.createContext<AuditContextValue>(createAuditContext());

export const useAuditContext = () => React.useContext(AuditContext);

export const AuditContextProvider: React.FC<Required<AuditContextValue>> = ({ location, children }) => {
    const ctx = useAuditContext();

    const chargedValue = React.useMemo(
        () => (location ? { ...ctx, location: mergeLocations(ctx.location, location) } : ctx),
        [ctx, location]
    );

    return <AuditContext.Provider value={chargedValue}>{children}</AuditContext.Provider>;
};

export const mergeLocations = (existing: string, location: string) => {
    if (!existing) {
        return location;
    }

    const startPattern = new RegExp(`^${existing}(/|$)`, "i");
    if (startPattern.test(location)) {
        return location;
    }

    return `${existing}/${location}`;
};

/** Used only for non-react environment, e.g. knockout details */
export const withAuditContextProvider =
    <T extends {}>(Component: React.ComponentType<T>) =>
    ({ auditContext, ...props }: T & { auditContext?: AuditContextValue }) =>
        (
            <AuditContextProvider location={auditContext?.location}>
                <Component {...(props as unknown as T)} />
            </AuditContextProvider>
        );

export const withAuditContext =
    <P extends object>(location: string, Component: React.ComponentType<P>) =>
    (props: P) =>
        (
            <AuditContextProvider location={location}>
                <Component {...props} />
            </AuditContextProvider>
        );
